import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import theme from 'theme';
import { useSnackbar } from 'notistack';
import { DenyDialog } from 'components/DenyDialog';
import { useAuth } from 'auth';

const RequestAction = ({
  userId,
  requestId,
  status,
  title,
  link,
  type,
  onApproveRequestAction,
  onDenyRequestAction,
  onCancelRequestAction,
  onCompleteRequestAction,
  requestedBy,
  description,
  totalAmount,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isHardwareManager, isOfficeManager } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [note, setNote] = useState('');

  const onApprove = useCallback(
    e => {
      e.preventDefault();
      setIsProcessing(true);
      onApproveRequestAction({
        requestId,
        userId,
        status: 'approved',
        requestedBy,
        title,
        link,
        type,
        description,
      })
        .then(() => {
          enqueueSnackbar('Education budget request successfully approved', {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar('Error when approving education budget request', {
            variant: 'error',
          });
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [
      onApproveRequestAction,
      requestId,
      userId,
      title,
      link,
      type,
      requestedBy,
      description,
      enqueueSnackbar,
    ],
  );

  const onDeny = useCallback(
    (e, addNote) => {
      e.preventDefault();
      setIsProcessing(true);
      onDenyRequestAction({
        requestId,
        userId,
        status: 'denied',
        requestedBy,
        title,
        link,
        type,
        description,
        note: addNote ? note : '',
      })
        .then(() => {
          enqueueSnackbar('Education budget request successfully denied', {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar('Error when denying education budget request', {
            variant: 'error',
          });
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [
      onDenyRequestAction,
      requestId,
      userId,
      requestedBy,
      title,
      link,
      type,
      description,
      enqueueSnackbar,
      note,
    ],
  );

  const onDenyOpenDialog = e => {
    e.preventDefault();
    setDialogOpen(true);
  };

  const onCancel = useCallback(
    e => {
      e.preventDefault();
      setIsProcessing(true);
      onCancelRequestAction({
        requestId,
        userId,
        status: 'canceled',
        requestedBy,
        title,
        link,
        type,
        description,
      })
        .then(() => {
          enqueueSnackbar('Education budget request successfully canceled', {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar('Error when canceling education budget request', {
            variant: 'error',
          });
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [
      onCancelRequestAction,
      requestId,
      userId,
      requestedBy,
      title,
      link,
      type,
      description,
      enqueueSnackbar,
    ],
  );

  const onComplete = useCallback(
    e => {
      e.preventDefault();
      setIsProcessing(true);

      onCompleteRequestAction({
        requestId,
        userId,
        status: 'completed',
        requestedBy,
        title,
        link,
        type,
        description,
        totalAmount,
      })
        .then(() => {
          enqueueSnackbar('Education budget request successfully completed', {
            variant: 'success',
          });
        })
        .catch(error => {
          const message =
            error?.message === 'Insufficient education budget'
              ? 'Sorry, there is not enough education budget for this request'
              : 'Error when completing education budget request';
          enqueueSnackbar(message, { variant: 'error' });
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [
      userId,
      totalAmount,
      onCompleteRequestAction,
      requestId,
      requestedBy,
      title,
      link,
      type,
      description,
      enqueueSnackbar,
    ],
  );

  if (isHardwareManager) {
    return <div />;
  }

  return (
    <div>
      {((status === 'pending' && !isOfficeManager) || status === 'approved') && (
        <Button
          variant="contained"
          size="small"
          disabled={isProcessing}
          onClick={e => (status === 'pending' ? onApprove(e) : onComplete(e))}
          sx={{
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: '#388e3c',
            },
          }}
          data-cy={status === 'pending' ? 'approveButton' : 'completeButton'}
        >
          {status === 'pending' ? 'Approve' : 'Complete'}
        </Button>
      )}
      {status !== 'completed' && (status === 'approved' || !isOfficeManager) && (
        <Button
          variant="contained"
          size="small"
          disabled={isProcessing}
          onClick={e => (status === 'pending' ? onDenyOpenDialog(e) : onCancel(e))}
          sx={{
            backgroundColor: theme.palette.danger.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: '#c62828',
            },
            marginLeft: '5px',
          }}
          data-cy={status === 'pending' ? 'denyButton' : 'cancelButton'}
        >
          {status === 'pending' ? 'Deny' : 'Cancel'}
        </Button>
      )}
      <DenyDialog
        setDialogOpen={setDialogOpen}
        type="education budget"
        setNote={setNote}
        onDeny={onDeny}
        isProcessing={isProcessing}
        isDialogOpen={isDialogOpen}
        note={note}
        requestedBy={requestedBy}
      />
    </div>
  );
};

RequestAction.propTypes = {
  userId: PropTypes.string.isRequired,
  requestId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  onApproveRequestAction: PropTypes.func.isRequired,
  onDenyRequestAction: PropTypes.func.isRequired,
  onCancelRequestAction: PropTypes.func.isRequired,
  onCompleteRequestAction: PropTypes.func.isRequired,
  requestedBy: PropTypes.any.isRequired,
  description: PropTypes.string.isRequired,
};

export default RequestAction;
